import { Fragment } from "react";
import { DEFAULT_STYLE, localized } from "./consentElements";
import {
  consentHeaderLayout,
  fieldLabel,
  headerFieldIsLabelled,
  placeholderOf,
  resolvePlaceholders,
} from "./consentFields";
import {
  blockStyle as contentBlockStyle,
  groupBlocks,
  isContentElement,
  blocksOf,
  runStyle,
} from "./consentContent";

const FONT_STACKS = {
  Helvetica: "Helvetica, Arial, sans-serif",
  Times: '"Times New Roman", Times, serif',
  Courier: '"Courier New", Courier, monospace',
};

const borderCss = (border) => {
  if (!border || border === "none") return "none";
  if (border === "box") return "1px solid #9E9E9E";
  return `1px ${border} #9E9E9E`;
};

export const blockStyle = (element) => {
  const style = { ...DEFAULT_STYLE, ...(element?.style || {}) };

  return {
    fontFamily: FONT_STACKS[style.fontFamily] || FONT_STACKS.Helvetica,
    fontSize: `${style.fontSize}pt`,
    fontWeight: style.fontWeight,
    fontStyle: style.fontStyle,
    textAlign: style.align,
    lineHeight: style.lineHeight,
    letterSpacing: `${style.letterSpacing}px`,
    color: style.color,
    background: style.background,
    border: borderCss(style.border),
    padding: `${style.paddingY}px ${style.paddingX}px`,
    marginTop: `${style.marginTop}px`,
    marginBottom: `${style.marginBottom}px`,
    width: `${style.width}%`,
  };
};

const Text = ({ value, mode, values }) => {
  if (mode === "preview") {
    return <>{resolvePlaceholders(value, values)}</>;
  }

  const parts = String(value ?? "").split(/(\{\{\s*[\w.]+\s*\}\})/g);

  return (
    <>
      {parts.map((part, index) =>
        /^\{\{\s*[\w.]+\s*\}\}$/.test(part) ? (
          <span className="consent-el__placeholder" key={index}>
            {part}
          </span>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        )
      )}
    </>
  );
};

const ContentBlocks = ({ element, mode, values }) => {
  const line = (block, index) => (
    <div key={index} style={contentBlockStyle(block)}>
      {block.runs.length ? (
        block.runs.map((run, runIndex) => (
          <span key={runIndex} style={runStyle(run)}>
            <Text value={run.text} mode={mode} values={values} />
          </span>
        ))
      ) : (
        <br />
      )}
    </div>
  );

  const groups = groupBlocks(blocksOf(element));

  if (!groups.length) {
    return <em style={{ opacity: 0.6 }}>No consent content yet</em>;
  }

  return (
    <>
      {groups.map((group, index) =>
        group.kind === "list" ? (
          <Fragment key={index}>
            {group.list === "ol" ? (
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                {group.items.map((block, itemIndex) => (
                  <li key={itemIndex}>{line(block, itemIndex)}</li>
                ))}
              </ol>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {group.items.map((block, itemIndex) => (
                  <li key={itemIndex}>{line(block, itemIndex)}</li>
                ))}
              </ul>
            )}
          </Fragment>
        ) : (
          line(group.block, index)
        )
      )}
    </>
  );
};

const FieldValue = ({ field, mode, values, fallback }) => {
  const placeholder = placeholderOf(field);

  if (mode === "preview") {
    const resolved = resolvePlaceholders(placeholder, values, fallback || "");
    return <span>{resolved || <span className="consent-el__line" />}</span>;
  }
  return <span className="consent-el__placeholder">{placeholder}</span>;
};

const Letterhead = ({ element, mode, values }) => {
  const { clinicName, clinicLines, doctorName, doctorLines } =
    consentHeaderLayout(element.props?.fields);

  const line = (field) => (
    <div className="consent-letterhead__line" key={field}>
      {headerFieldIsLabelled(field) ? (
        <span className="consent-el__field-label">{fieldLabel(field)}:</span>
      ) : null}
      <FieldValue field={field} mode={mode} values={values} />
    </div>
  );

  const showLogo = element.props?.showLogo !== false;
  const logoHeight = Number(element.props?.logoHeight) || 18;

  return (
    <div className="consent-letterhead">
      <div
        className="consent-letterhead__clinic"
        data-testid="consent-letterhead-clinic"
      >
        {showLogo ? (
          <div
            className="consent-el__logo consent-letterhead__logo"
            style={{ height: `${logoHeight}mm`, width: `${logoHeight}mm` }}
          >
            Logo
          </div>
        ) : null}
        <div className="consent-letterhead__body">
          {clinicName ? (
            <div className="consent-letterhead__name">
              <FieldValue field={clinicName} mode={mode} values={values} />
            </div>
          ) : null}
          {clinicLines.map(line)}
        </div>
      </div>

      {doctorName || doctorLines.length ? (
        <div
          className="consent-letterhead__doctor"
          data-testid="consent-letterhead-doctor"
        >
          {doctorName ? (
            <div className="consent-letterhead__name">
              <FieldValue field={doctorName} mode={mode} values={values} />
            </div>
          ) : null}
          {doctorLines.map(line)}
        </div>
      ) : null}
    </div>
  );
};

const FieldRows = ({ element, lang, mode, values }) => {
  const fields = element.props?.fields || [];
  const columns = Math.max(1, Number(element.props?.columns) || 1);
  const appointmentFields = element.props?.previewAppointmentFields || [];
  const displayFields = fields.flatMap((field) =>
    field === "patient.age" && appointmentFields.length
      ? [field, "__previewDateTime"]
      : [field]
  );
  if (appointmentFields.length && !fields.includes("patient.age")) {
    displayFields.push("__previewDateTime");
  }

  const appointmentDateTime = appointmentFields
    .map((field) => resolvePlaceholders(placeholderOf(field), values))
    .filter(Boolean)
    .join(", ");

  return (
    <div className="consent-el">
      {element.props?.showHeading !== false && localized(element.content, lang) ? (
        <div style={{ fontWeight: "bold", marginBottom: 3 }}>
          <Text value={localized(element.content, lang)} mode={mode} values={values} />
        </div>
      ) : null}
      <div
        className="consent-el__fields"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {displayFields.length ? (
          displayFields.map((field) => (
            <div
              key={field}
              className={field === "__previewDateTime" ? "consent-el__field--date-time" : undefined}
            >
              <span className="consent-el__field-label">
                {field === "__previewDateTime"
                  ? "Date & Time"
                  : `${fieldLabel(field)}${element.type === "patientInfo" ? "" : ":"}`}
              </span>
              {field === "__previewDateTime" ? (
                <span className="consent-el__field-value--date-time">
                  {appointmentDateTime}
                </span>
              ) : (
                <FieldValue field={field} mode={mode} values={values} />
              )}
            </div>
          ))
        ) : (
          <em style={{ opacity: 0.6 }}>No fields selected</em>
        )}
      </div>
    </div>
  );
};

const LabelledValue = ({ element, lang, mode, values }) => {
  const label = localized(element.label, lang);
  const inline = element.props?.inline !== false;

  return (
    <div className="consent-el">
      {label ? (
        <span className="consent-el__field-label">{label}:</span>
      ) : null}
      {inline ? " " : <br />}
      <FieldValue
        field={element.field}
        mode={mode}
        values={values}
        fallback={element.props?.fallback}
      />
    </div>
  );
};

const ControlLabel = ({ element, lang, mode, values }) => {
  const label = localized(element.label, lang);
  if (!label) return null;

  return (
    <span className="consent-el__field-label">
      <Text value={label} mode={mode} values={values} />
      {element.props?.required ? " *" : ""}
    </span>
  );
};

const Options = ({ element, lang, glyph, mode, values }) => (
  <div
    style={{
      display: element.props?.inline ? "flex" : "block",
      gap: 18,
      flexWrap: "wrap",
    }}
  >
    {(element.options || []).map((option) => (
      <div key={option.value}>
        <span className="consent-el__tick">{glyph}</span>
        <Text value={localized(option.label, lang)} mode={mode} values={values} />
      </div>
    ))}
  </div>
);

const ConsentElementView = ({
  element,
  lang = "en",
  mode = "design",
  values,
}) => {
  const style = blockStyle(element);
  const content = localized(element.content, lang);

  const wrap = (children, extra) => (
    <div
      className={`consent-el${element.type === "patientInfo" ? " consent-el--patient-info" : ""}`}
      style={{ ...style, ...extra }}
    >
      {children}
    </div>
  );

  switch (element.type) {
    case "heading":
    case "subHeading":
    case "paragraph":
      return wrap(<Text value={content} mode={mode} values={values} />);

    case "richText": {
      // The simplified builder's content block. A `richText` written by the old
      // builder has no `format` and keeps the plain-line rendering below.
      if (isContentElement(element)) {
        return wrap(
          <ContentBlocks element={element} mode={mode} values={values} />
        );
      }

      const lines = String(content || "").split("\n");
      const bulleted = element.props?.listStyle === "bullet";

      return wrap(
        bulleted ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {lines.map((line, index) => (
              <li key={index}>
                <Text value={line} mode={mode} values={values} />
              </li>
            ))}
          </ul>
        ) : (
          lines.map((line, index) => (
            <div key={index}>
              <Text value={line} mode={mode} values={values} />
            </div>
          ))
        )
      );
    }

    case "divider":
      return (
        <div style={{ ...style, padding: 0 }}>
          <hr
            style={{
              border: "none",
              borderTop: `${element.props?.thickness || 1}px solid ${
                element.props?.color || "#9E9E9E"
              }`,
              margin: 0,
            }}
          />
        </div>
      );

    case "spacer":
      return (
        <div
          style={{ height: `${element.props?.height || 12}px` }}
          aria-hidden="true"
        />
      );

    case "pageBreak":
      // Preview splits sheets on this element, so it only ever draws on canvas.
      return <div className="consent-el__break">— page break —</div>;

    case "clinicInfo":
      return wrap(<Letterhead element={element} mode={mode} values={values} />);

    case "patientInfo":
    case "appointmentInfo":
    case "doctorInfo":
      return wrap(
        <FieldRows element={element} lang={lang} mode={mode} values={values} />
      );

    case "diagnosis":
    case "treatment":
    case "procedure":
    case "toothInfo":
    case "cost":
    case "dynamicField":
      return wrap(
        <LabelledValue
          element={element}
          lang={lang}
          mode={mode}
          values={values}
        />
      );

    case "textField":
    case "number":
    case "date":
      return wrap(
        <div>
          <ControlLabel element={element} lang={lang} mode={mode} values={values} />{" "}
          <span className="consent-el__line" style={{ minWidth: 160 }} />
          {element.type === "date" && element.props?.format ? (
            <span style={{ opacity: 0.6, marginLeft: 6 }}>
              ({element.props.format})
            </span>
          ) : null}
        </div>
      );

    case "textArea":
      return wrap(
        <div>
          <ControlLabel element={element} lang={lang} mode={mode} values={values} />
          <div
            className="consent-el__box"
            style={{ minHeight: `${(element.props?.lines || 3) * 1.6}em` }}
          />
        </div>
      );

    case "checkbox":
      return wrap(
        <div>
          <span className="consent-el__tick">☐</span>
          <ControlLabel element={element} lang={lang} mode={mode} values={values} />
        </div>
      );

    case "checkboxGroup":
      return wrap(
        <div>
          <ControlLabel element={element} lang={lang} mode={mode} values={values} />
          <Options
            element={element}
            lang={lang}
            glyph="☐"
            mode={mode}
            values={values}
          />
        </div>
      );

    case "radioGroup":
      return wrap(
        <div>
          <ControlLabel element={element} lang={lang} mode={mode} values={values} />
          <Options
            element={element}
            lang={lang}
            glyph="○"
            mode={mode}
            values={values}
          />
        </div>
      );

    case "select":
      return wrap(
        <div>
          <ControlLabel element={element} lang={lang} mode={mode} values={values} />{" "}
          <span className="consent-el__line" style={{ minWidth: 160 }} />
          <span style={{ opacity: 0.6, marginLeft: 6 }}>
            (
            {(element.options || [])
              .map((option) => localized(option.label, lang))
              .filter(Boolean)
              .join(" / ")}
            )
          </span>
        </div>
      );

    case "image":
      return wrap(
        element.props?.url ? (
          <img
            src={element.props.url}
            alt={content || "Consent image"}
            style={{
              height: `${element.props?.height || 40}mm`,
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <div
            className="consent-el__logo"
            style={{ height: `${element.props?.height || 40}mm` }}
          >
            {content || "Image"} — no URL set
          </div>
        ),
        { textAlign: element.props?.align || style.textAlign }
      );

    case "clinicLogo":
      return wrap(
        <div
          className="consent-el__logo"
          style={{ height: `${element.props?.height || 20}mm` }}
        >
          Clinic logo
        </div>,
        { textAlign: element.props?.align || "center" }
      );

    case "signature":
      return wrap(
        <div style={{ width: `${element.props?.width || 60}%` }}>
          <div className="consent-el__signature-line" />
          <div>{localized(element.label, lang)}</div>
          {element.props?.showDate ? <div>Date: ____________</div> : null}
        </div>
      );

    case "consentDeclaration":
      return wrap(
        <div>
          <div style={{ marginBottom: 6 }}>
            <Text value={content} mode={mode} values={values} />
          </div>
          {element.props?.requireAcknowledgement === false ? null : (
            <div>
              <span className="consent-el__tick">☐</span>
              <Text
                value={localized(element.label, lang)}
                mode={mode}
                values={values}
              />
            </div>
          )}
        </div>
      );

    case "signatureBlock":
      return wrap(
        <div className="consent-el__signatures">
          {(element.props?.signatories || []).map((signatory, index) => (
            <div
              className="consent-el__signature"
              key={signatory.key || index}
            >
              <div className="consent-el__signature-line" />
              <div>{localized(signatory.label, lang)}</div>
              {signatory.showDate ? <div>Date: ____________</div> : null}
            </div>
          ))}
        </div>
      );

    case "customField":
      return wrap(
        <div>
          <ControlLabel element={element} lang={lang} mode={mode} values={values} />{" "}
          <span className="consent-el__line" style={{ minWidth: 160 }} />
        </div>
      );

    default:
      // An element type this build does not know (a newer design): say so
      // rather than dropping it silently, so nothing disappears from a consent.
      return wrap(
        <em style={{ opacity: 0.7 }}>
          Unsupported element "{element.type}" — open this consent in a newer
          build to edit it.
        </em>
      );
  }
};

export default ConsentElementView;
